<template>
  <div class="approval-detail pa-4" data-testid="approvalDetail-container-root">
    <div
      v-if="hasMenuOptions"
      class="d-flex justify-end"
      data-testid="approvalDetail-header-actions"
    >
      <Menu :options="menuOptions">
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="blue-grey"
            icon
            small
            data-testid="approvalDetail-button-options"
          >
            <v-icon size="20">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
      </Menu>
    </div>

    <div
      v-if="approval && approval.status === APPROVAL_STATUS.REQUESTED"
      data-testid="approvalDetail-state-pending"
    >
      <EmptyState
        icon="mdi-clock-outline"
        :message="
          $t('routines.approval.approval_requested_to', {
            approver: approverName,
            requester: requesterName,
          })
        "
        :title="$t('routines.approval.approval_request_pending_message')"
      />

      <div
        :class="`d-flex ${actionAlignment} mt-4`"
        data-testid="approvalDetail-actions-requested"
      >
        <Button
          v-if="canCancelRequest"
          color="grey"
          data-testid="approvalDetail-button-cancelRequest"
          plain
          :loading="isCancelling"
          :text="$t('routines.approval.cancel_request')"
          @click="$emit('cancelRequest')"
        />

        <div>
          <Button
            v-if="canRejectRequest"
            class="me-4"
            color="danger"
            data-testid="approvalDetail-button-rejectRequest"
            icon="mdi-clipboard-remove-outline"
            outlined
            :text="$t('common.button.reject')"
            @click="$emit('rejectRequest')"
          />

          <Button
            v-if="canApproveRequest"
            color="primary"
            data-testid="approvalDetail-button-approveRequest"
            icon="mdi-clipboard-check-outline"
            :text="$t('common.approve')"
            @click="$emit('approveRequest')"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="approval && approval.status === APPROVAL_STATUS.APPROVED"
      data-testid="approvalDetail-state-approved"
    >
      <div class="d-flex flex-column">
        <FieldLabel :text="$t('routines.approval.approved_by')" />
        <ChipAvatar class="mt-1" small :account="approval.approver" />
      </div>

      <div class="d-flex flex-column mt-4">
        <FieldLabel :text="$t('routines.approval.approved_on')" />
        <p
          class="body-2 mt-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
        >
          {{ approvedAt }}
        </p>
      </div>

      <div class="d-flex flex-column mt-6">
        <FieldLabel :text="$t('common.requested_by')" />
        <ChipAvatar class="mt-1" small :account="approval.createdBy" />
      </div>

      <div class="d-flex flex-column mt-4">
        <FieldLabel :text="$t('common.requested_on')" />
        <p
          class="body-2 mt-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
        >
          {{ createdAt }}
        </p>
      </div>

      <div
        v-if="hasMedia"
        class="d-flex flex-column mt-4"
        data-testid="approvalDetail-section-evidence"
      >
        <FieldLabel :text="$t('common.evidence')" />
        <MediaContainer
          class="mt-2"
          onlyThumbnails
          readOnly
          :images="approval.media"
        />
      </div>

      <div
        v-if="hasAttachments"
        class="d-flex flex-column mt-6"
        data-testid="approvalDetail-section-attachments"
      >
        <FieldLabel :text="$t('attachments.attachments')" />
        <ThumbnailAttachments
          class="mt-2"
          readOnly
          :attachments="detailAttachments"
        />
      </div>

      <div
        v-if="hasNotes"
        class="d-flex flex-column mt-6"
        data-testid="approvalDetail-section-notes"
      >
        <FieldLabel :text="$t('routines.note_other')" />

        <p class="body-2 blue-grey--text text--darken-3 pre-line mt-0 mb-0">
          {{ approval.notes }}
        </p>
      </div>

      <div
        v-if="signature"
        class="d-flex flex-column mt-8"
        data-testid="approvalDetail-section-signature"
      >
        <FieldLabel :text="$t('checklist.types.signature')" />
        <ApprovalSignatureField
          readOnly
          class="mt-2"
          :account="approval.approver"
          :loading="false"
          :previewHeight="156"
          :signature="signature"
        />
      </div>
    </div>

    <div
      v-else-if="approval && approval.status === APPROVAL_STATUS.REJECTED"
      data-testid="approvalDetail-state-rejected"
    >
      <div class="d-flex flex-column">
        <FieldLabel :text="$t('common.requested_by')" />
        <ChipAvatar class="mt-1" small :account="approval.createdBy" />

        <FieldLabel class="mt-4" :text="$t('common.requested_on')" />
        <p
          class="body-3 mt-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
        >
          {{ createdAt }}
        </p>
      </div>

      <div class="d-flex flex-column mt-6">
        <FieldLabel :text="$t('routines.approval.rejected_by')" />
        <ChipAvatar class="mt-1" small :account="rejectedByAccount" />

        <FieldLabel class="mt-4" :text="$t('routines.approval.rejected_on')" />
        <p
          class="body-3 mt-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
        >
          {{ rejectedAt }}
        </p>
      </div>

      <Alert
        class="mt-6"
        data-testid="approvalDetail-section-rejectionReason"
        :vuetifyProps="{
          color: 'red',
          icon: 'mdi-close-circle',
        }"
        :text="
          $t('routines.approval.rejection_reason_text', {
            reason: rejectionReason,
          })
        "
      />

      <div
        class="d-flex justify-center mt-6"
        data-testid="approvalDetail-actions-rejected"
      >
        <Button
          v-if="canReRequest"
          color="primary"
          data-testid="approvalDetail-button-reRequest"
          icon="mdi-refresh"
          :loading="isReRequesting"
          :text="$t('routines.approval.re_request')"
          @click="$emit('reRequest')"
        />
      </div>
    </div>

    <RoutineApprovalDialog
      v-if="approval"
      :approval="approval"
      :routine="routine"
      :routineId="routine.id"
      :show="isEditDialogOpen"
      data-testid="approvalDetail-dialog-editApproval"
      @close="closeEditDialog"
      @updated="handleEditUpdated"
    />

    <RoutineApprovalHistoryDialog
      v-if="approval"
      :approval="approval"
      :history="approval.history"
      :show="isHistoryDialogOpen"
      data-testid="approvalDetail-dialog-history"
      @close="closeHistoryDialog"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import routineApprovalPermissions from '@/mixins/routineApprovalPermissions.vue'

import Alert from '@/components/Alert.vue'
import Button from '@/components/UI/Button.vue'
import Menu from '@/components/UI/Menu.vue'
import FieldLabel from '@/components/Core/FieldLabel.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import EmptyState from '@/components/EmptyState.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import ThumbnailAttachments from '@/components/ThumbnailAttachments/ThumbnailAttachments.vue'
import SignatureForm from '@/components/Checklists/ChecklistReply/SignatureForm.vue'
import { buildAccountFullName } from '@/components/Checklists/ChecklistReply/signatureUtils'
import RoutineApprovalDialog from '@/components/Routines/RoutineApproval/RoutineApprovalDialog.vue'
import RoutineApprovalHistoryDialog from '@/components/Routines/RoutineApproval/RoutineApprovalHistoryDialog.vue'
import { APPROVAL_STATUS } from './constants'

export default {
  name: 'ApprovalDetail',

  components: {
    Alert,
    ApprovalSignatureField: SignatureForm,
    Button,
    ChipAvatar,
    EmptyState,
    FieldLabel,
    MediaContainer,
    Menu,
    RoutineApprovalDialog,
    RoutineApprovalHistoryDialog,
    ThumbnailAttachments,
  },

  mixins: [mixin, routineApprovalPermissions],

  emits: [
    'approveRequest',
    'cancelRequest',
    'rejectRequest',
    'reRequest',
    'updated',
  ],

  props: {
    approval: {
      type: Object,
      default: null,
    },
    routine: {
      type: Object,
      required: true,
    },
    isCancelling: Boolean,
    isReRequesting: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      detailAttachments: [],
      isEditDialogOpen: false,
      isHistoryDialogOpen: false,
      isLoadingAttachments: false,
      APPROVAL_STATUS,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    approvalPermissions() {
      return this.getRoutineApprovalPermissions({
        routineApproval: this.approval,
        routine: this.routine,
      })
    },

    canApproveRequest() {
      return this.approvalPermissions.approve
    },

    canCancelRequest() {
      return this.approvalPermissions.cancel
    },

    canRejectRequest() {
      return this.approvalPermissions.reject
    },

    canReRequest() {
      return this.approvalPermissions.reRequest
    },

    canEditApproval() {
      return this.approvalPermissions.edit
    },

    actionAlignment() {
      const visibleActions = [
        this.canApproveRequest,
        this.canCancelRequest,
        this.canRejectRequest,
      ].filter(Boolean).length

      if (visibleActions > 2) {
        return 'justify-space-between'
      }

      if (visibleActions === 2) {
        return 'justify-end'
      }

      return 'justify-center'
    },

    menuOptions() {
      return _.compact([
        this.canEditApproval && {
          id: 'editApproval',
          icon: 'mdi-pencil-outline',
          testId: 'approvalDetail-menuItem-editApproval',
          title: this.$t('common.button.edit'),
          action: () => this.openEditDialog(),
        },
        Boolean(this.approval?.id) && {
          id: 'history',
          icon: 'mdi-history',
          testId: 'approvalDetail-menuItem-history',
          title: this.$t('routines.approval.history'),
          action: () => this.openHistoryDialog(),
        },
      ])
    },

    hasMenuOptions() {
      return !_.isEmpty(this.menuOptions)
    },

    shouldFetchAttachments() {
      return (
        this.approval?.status === APPROVAL_STATUS.APPROVED &&
        Boolean(this.approval?.id)
      )
    },

    approverName() {
      return buildAccountFullName(this.approval?.approver) || '-'
    },

    requesterName() {
      return buildAccountFullName(this.approval?.createdBy) || '-'
    },

    createdAt() {
      return utils.dates.formatDate(
        this.approval?.createdAt,
        'MMM D YYYY, h:mm A'
      )
    },

    approvedAt() {
      return utils.dates.formatDate(
        this.approval?.statusUpdatedAt,
        'MMM D YYYY, h:mm A'
      )
    },

    rejectedAt() {
      return utils.dates.formatDate(
        this.approval?.statusUpdatedAt,
        'MMM D YYYY, h:mm A'
      )
    },

    rejectedByAccount() {
      return this.approval?.approver
    },

    rejectionReason() {
      return this.approval?.rejectionReason?.trim?.() || ''
    },

    hasAttachments() {
      return !_.isEmpty(this.detailAttachments)
    },

    hasMedia() {
      return !_.isEmpty(this.approval?.media)
    },

    hasNotes() {
      return !_.isEmpty(this.approval?.notes?.trim?.())
    },

    signature() {
      return this.approval?.signature || null
    },
  },

  watch: {
    approval: {
      immediate: true,
      handler() {
        this.refreshDetailAttachments()
      },
    },
  },

  methods: {
    async refreshDetailAttachments() {
      this.detailAttachments = []

      if (!this.shouldFetchAttachments) {
        return
      }

      this.isLoadingAttachments = true

      try {
        const response = await api.routine.getRoutineApprovalAttachments(
          this.organizationId,
          this.approval.id
        )

        this.detailAttachments = [...(response?.data || [])]
      } catch (error) {
        this.detailAttachments = []
        this.handleRequestError(error)
      } finally {
        this.isLoadingAttachments = false
      }
    },

    openEditDialog() {
      if (!this.approval?.id) {
        return
      }

      this.isEditDialogOpen = true
    },

    closeEditDialog() {
      this.isEditDialogOpen = false
    },

    openHistoryDialog() {
      if (!this.approval?.id) {
        return
      }

      this.isHistoryDialogOpen = true
    },

    closeHistoryDialog() {
      this.isHistoryDialogOpen = false
    },

    handleEditUpdated(updatedApproval) {
      this.isEditDialogOpen = false
      this.$emit('updated', updatedApproval)
    },
  },
}
</script>
